const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const clampChannel = (value: number) => Math.min(255, Math.max(0, Math.round(value)))

export const isHexColor = (value: string) => HEX_COLOR_RE.test(value)

export const expandHexColor = (value: string) => {
  const normalized = value.replace('#', '')
  if (normalized.length === 3) {
    return normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('')
  }
  return normalized
}

export const normalizeThemeColor = (value: unknown, fallback: string) => {
  const normalized = String(value ?? '').trim()
  if (!normalized) return fallback
  return isHexColor(normalized) ? normalized.toLowerCase() : fallback
}

export const hexToRgb = (hexColor: string) => {
  const expanded = expandHexColor(hexColor)
  return {
    red: Number.parseInt(expanded.slice(0, 2), 16),
    green: Number.parseInt(expanded.slice(2, 4), 16),
    blue: Number.parseInt(expanded.slice(4, 6), 16),
  }
}

export const hexToRgba = (hexColor: string, alpha: number) => {
  const { red, green, blue } = hexToRgb(hexColor)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export const mixHexColors = (baseHex: string, targetHex: string, ratio: number) => {
  const safeRatio = Math.min(1, Math.max(0, ratio))
  const base = hexToRgb(baseHex)
  const target = hexToRgb(targetHex)

  const red = clampChannel(base.red + ((target.red - base.red) * safeRatio))
  const green = clampChannel(base.green + ((target.green - base.green) * safeRatio))
  const blue = clampChannel(base.blue + ((target.blue - base.blue) * safeRatio))

  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

export const lightenHexColor = (hexColor: string, ratio: number) => mixHexColors(hexColor, '#ffffff', ratio)
export const darkenHexColor = (hexColor: string, ratio: number) => mixHexColors(hexColor, '#000000', ratio)

const toSrgbChannel = (channel: number) => {
  const normalized = channel / 255
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4
}

const getRelativeLuminance = (hexColor: string) => {
  const { red, green, blue } = hexToRgb(hexColor)
  return (
    0.2126 * toSrgbChannel(red) +
    0.7152 * toSrgbChannel(green) +
    0.0722 * toSrgbChannel(blue)
  )
}

export const getContrastRatio = (foregroundHex: string, backgroundHex: string) => {
  const foregroundLuminance = getRelativeLuminance(foregroundHex)
  const backgroundLuminance = getRelativeLuminance(backgroundHex)
  const lighter = Math.max(foregroundLuminance, backgroundLuminance)
  const darker = Math.min(foregroundLuminance, backgroundLuminance)
  return (lighter + 0.05) / (darker + 0.05)
}

export const resolveAccessibleForegroundColor = (
  preferredHex: string,
  backgroundHex: string,
  minimumContrast = 4.5
) => {
  if (!isHexColor(preferredHex) || !isHexColor(backgroundHex)) {
    return resolveContrastColor(backgroundHex)
  }

  if (getContrastRatio(preferredHex, backgroundHex) >= minimumContrast) {
    return preferredHex.toLowerCase()
  }

  const targetColor = resolveContrastColor(backgroundHex)
  let lowerRatio = 0
  let upperRatio = 1

  for (let iteration = 0; iteration < 16; iteration += 1) {
    const candidateRatio = (lowerRatio + upperRatio) / 2
    const candidateColor = mixHexColors(preferredHex, targetColor, candidateRatio)

    if (getContrastRatio(candidateColor, backgroundHex) >= minimumContrast) {
      upperRatio = candidateRatio
    } else {
      lowerRatio = candidateRatio
    }
  }

  return mixHexColors(preferredHex, targetColor, upperRatio)
}

export const resolveAccessibleTint = (
  accentHex: string,
  tintRatio = 0.1,
  minimumContrast = 4.5
) => {
  const backgroundColor = mixHexColors('#ffffff', accentHex, tintRatio)
  return {
    backgroundColor,
    color: resolveAccessibleForegroundColor(accentHex, backgroundColor, minimumContrast),
  }
}

export const resolveContrastColor = (hexColor: string) => {
  if (!isHexColor(hexColor)) return '#ffffff'
  const darkText = '#111827'
  const lightText = '#ffffff'
  return getContrastRatio(darkText, hexColor) >= getContrastRatio(lightText, hexColor)
    ? darkText
    : lightText
}
